import { useEffect, useState } from 'react';
import type { IconName } from '@cencosud-cencommerce/paris-uikit';
import { Icon } from '@cencosud-cencommerce/paris-uikit';

import {
  type AccordionIndex,
  TPaymentMethodId
} from '@/modules/perseo-billing/components/accordion/interfaces';
import { useAccordion } from '@/modules/perseo-billing/components/accordion/use-accordion';
import { useGetCartTotals } from '@/modules/perseo-billing/hooks';
import type { ISavedCardResponse } from '@/modules/perseo-billing/interfaces';

import { ShowPromoText } from '../../payment-methods-header';
import { DISABLED_STYLE } from '../accordion-template.component';

import { CardExpired } from './components/saved-card-expired.component';
import { CardSaving } from './components/saved-card-saving.component';
import { CardType } from './components/saved-card-type.component';

type TSavedCardProps = {
  savedCard: ISavedCardResponse;
  isActive: boolean;
  accordionIndexes: AccordionIndex;
  isEnabled: boolean;
};

const iconCardsMapper: Record<string, IconName> = {
  visa: 'pm-visa',
  mastercard: 'pm-mastercard',
  americanexpress: 'pm-americanexpress',
  [TPaymentMethodId.EPAY_CENCOSUD_TCOM]: 'pm-cencosud-card'
};

export const SavedCardHeader = (props: TSavedCardProps) => {
  const { savedCard, isActive, accordionIndexes } = props;
  const { onHeaderClick } = useAccordion();
  const handleClick = () => onHeaderClick(accordionIndexes, savedCard.methodId);
  const [saveCardIcon, setSavedCardIcon] = useState<IconName>('credit-card');
  const { otherMethods, cencosudCard } = useGetCartTotals();

  const isCencosudCard =
    savedCard.methodId === TPaymentMethodId.EPAY_CENCOSUD_TCOM;
  const isExpired = savedCard?.isExpired ?? false;
  const cencosudSaveAmount = otherMethods?.amount - cencosudCard?.amount;

  const className = props.isEnabled ? '' : DISABLED_STYLE;

  useEffect(() => {
    const { brand, methodId } = savedCard;

    const icon = iconCardsMapper[methodId] || iconCardsMapper[brand];
    if (icon) {
      setSavedCardIcon(icon);
    }
  }, [savedCard]);

  const roundedClass = isActive ? 'co-rounded-t-[0.4rem]' : 'co-rounded';
  return (
    <button
      onClick={handleClick}
      className={`co-w-full co-bg-saved-creditCard ${roundedClass}`}
    >
      <div className={`co-w-full co-relative co-overflow-hidden ${className}`}>
        <div
          className={`co-absolute co-w-full co-h-full co-left-[30%] co-bg-saved-card-mask ${roundedClass}`}
          style={{ clipPath: 'circle(50.0% at 75% 50%)' }}
        ></div>
        <div
          className={`co-w-full co-flex co-p-16px lg:co-px-24px  co-gap-8px co-text-[#0E2B69] co-items-center ${roundedClass}`}
        >
          <div className="co-w-full co-flex co-gap-24px co-items-center">
            <Icon name={saveCardIcon} width={35} height={24} />
            <CardType disabled={!savedCard.bin} isCreditCard={true} />
            <p className="co-fill-current co-font-medium co-text-[#0E2B69]">
              **** {savedCard?.lastDigits}
            </p>
            <div className="co-ml-auto co-mr-3 co-flex co-items-center">
              {cencosudSaveAmount > 0 && (
                <ShowPromoText id={savedCard.methodId} />
              )}
              {isExpired ? (
                <CardExpired />
              ) : isCencosudCard ? (
                <CardSaving isCencosudCard={isCencosudCard} />
              ) : null}
            </div>
          </div>
          <Icon
            color="black"
            width={24}
            height={24}
            name={isActive ? 'arrow-up' : 'arrow-down'}
          />
        </div>
      </div>
    </button>
  );
};
